import { FC, useEffect, useState } from 'react';
import { Button } from '@carbon/react';
import { Renew } from '@carbon/react/icons';
import styles from './page.module.scss';

const SECOND_MS = 1000;

interface RefreshControlProps {
  /**
   * Whether a poll is scheduled. Polling only runs while a row on the page can still move, so this
   * goes false the moment everything has settled. That is the case the readout exists for: without
   * it the page simply stops updating and looks identical to one that is up to date.
   */
  autoRefreshing: boolean;
  /**
   * Epoch ms the next poll is due, or null when none is scheduled. The polling hook chains the next
   * tick only once the previous one settles, so this is when that tick actually fires rather than
   * an estimate of it.
   */
  nextPollAt: number | null;
  refreshing: boolean;
  onRefresh: () => void;
}

function statusText(
  autoRefreshing: boolean,
  secondsLeft: number | null,
  refreshing: boolean
): string {
  if (refreshing) return 'Refreshing…';
  if (!autoRefreshing) return 'Auto-refresh paused, nothing running';
  if (secondsLeft === null) return 'Auto-refresh on';
  return `Next auto-refresh in ${secondsLeft}s`;
}

export const RefreshControl: FC<RefreshControlProps> = ({
  autoRefreshing,
  nextPollAt,
  refreshing,
  onRefresh,
}) => {
  const [now, setNow] = useState(() => Date.now());

  // One tick a second, and only while a countdown is on screen. A settled page schedules no timer
  // at all rather than re-rendering every second to display a fixed string.
  useEffect(() => {
    if (!autoRefreshing || nextPollAt === null) return undefined;

    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), SECOND_MS);
    return () => clearInterval(id);
  }, [autoRefreshing, nextPollAt]);

  const secondsLeft =
    nextPollAt === null
      ? null
      : Math.max(0, Math.ceil((nextPollAt - now) / SECOND_MS));

  return (
    <div className={styles.refreshControl}>
      <span className={styles.refreshStatus}>
        {statusText(autoRefreshing, secondsLeft, refreshing)}
      </span>
      <Button
        kind="ghost"
        size="sm"
        renderIcon={Renew}
        onClick={onRefresh}
        disabled={refreshing}
      >
        Refresh
      </Button>
    </div>
  );
};
